import { Link } from "wouter";
import { faqData } from "@/lib/faq-data";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useStructuredData } from "@/hooks/useStructuredData";
import { useLocale } from "@/hooks/useLocale";

export default function FaqPage() {
  const { localizedHref } = useLocale();
  usePageMeta({
    title: "Frequently Asked Questions",
    description:
      "Answers about privacy, pricing, accounts, and how the tools on FreeDailyPro actually work.",
    path: "/faq",
  });

  useStructuredData({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.flatMap((cat) =>
      cat.items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      }))
    ),
  });

  return (
    <div className="container max-w-3xl mx-auto py-12 px-4">
      <nav className="text-sm text-muted-foreground mb-6">
        <Link href={localizedHref("/")} className="hover:text-foreground">
          Home
        </Link>{" "}
        <span className="mx-1">→</span> FAQ
      </nav>

      <h1 className="text-3xl font-bold mb-3 gradient-text">Frequently asked questions</h1>
      <p className="text-muted-foreground mb-10">
        Everything about privacy, pricing, accounts, and how the tools
        actually work.
      </p>

      <div className="space-y-10">
        {faqData.map(group => (
          <div key={group.category}>
            <h2 className="text-xs font-semibold uppercase tracking-wide text-primary mb-3">
              {group.category}
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {group.items.map((item, i) => (
                <AccordionItem key={i} value={`${group.category}-${i}`}>
                  <AccordionTrigger className="text-left">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent>{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}
      </div>

      <div className="mt-12 p-5 rounded-xl border border-border bg-card text-center">
        <p className="text-sm text-muted-foreground mb-1">
          Still have a question?
        </p>
        <Link href={localizedHref("/contact")} className="text-sm font-medium text-primary hover:underline">
          Get in touch →
        </Link>
      </div>
    </div>
  );
}
